/**
 * The arithmetic and the wording behind a bill in progress.
 *
 * Split out of the two screens that show one because they have to agree.
 * `/bills` and `/bill/:id` draw the same ladder, and a card that says a bill
 * passed one chamber next to a page that says something else is worse than
 * either being wrong on its own.
 *
 * Nothing here invents a fact. Where the server has not said how far a bill
 * has got, these functions return "not known" rather than reading it off the
 * roll calls themselves — that inference belongs in one place, and the list
 * page already sources it there.
 */

import type { BillDetail, Explainer, Odds, Rung, Stage } from "../api";

/** Warmer as a bill gets closer, so a list reads at a glance. */
export const TONE: Record<string, { chip: string; bar: string; text: string; ring: string }> = {
  law: {
    chip: "bg-brand/12 text-brand",
    bar: "bg-brand",
    text: "text-brand",
    ring: "border-brand",
  },
  "both-chambers": {
    chip: "bg-brand/12 text-brand",
    bar: "bg-brand",
    text: "text-brand",
    ring: "border-brand",
  },
  "one-chamber": {
    chip: "bg-amber-500/12 text-amber-700",
    bar: "bg-amber-500",
    text: "text-amber-700",
    ring: "border-amber-500",
  },
  reported: {
    chip: "bg-sky-500/12 text-sky-700",
    bar: "bg-sky-500",
    text: "text-sky-700",
    ring: "border-sky-500",
  },
  committee: { chip: "bg-sunk text-muted", bar: "bg-line", text: "text-muted", ring: "border-line" },
  introduced: {
    chip: "bg-sunk text-muted",
    bar: "bg-line",
    text: "text-muted",
    ring: "border-line",
  },
};

export const toneFor = (key: string) => TONE[key] ?? TONE.committee;

/** A rate people can hold in their head. 0.332 is "1 in 3". */
export function oneIn(share: number): string {
  if (share <= 0) return "None";
  if (share >= 0.95) return "Nearly all";
  if (share >= 0.5) return `${Math.round(share * 100)}%`;
  return `1 in ${Math.round(1 / share)}`;
}

/** The same rate as a percentage, for when it is shown beside the count it
 *  came from and the reader is meant to be able to compare two of them. */
export function asPercent(share: number): string {
  const whole = share * 100;
  return `${whole < 10 ? Math.round(whole * 10) / 10 : Math.round(whole)}%`;
}

/** congress.gov spells the type out in its urls. */
export function slug(kind: string): string {
  return (
    {
      hr: "house-bill",
      s: "senate-bill",
      hjres: "house-joint-resolution",
      sjres: "senate-joint-resolution",
      hconres: "house-concurrent-resolution",
      sconres: "senate-concurrent-resolution",
      hres: "house-resolution",
      sres: "senate-resolution",
    }[kind] ?? "house-bill"
  );
}

/** Where congress.gov publishes this bill. Built from the parts when the
 *  payload carries no `url`, so the link is never missing. */
export function congressUrl(bill: {
  congress: number;
  bill_type: string;
  number: number;
  url?: string | null;
}): string {
  if (bill.url) return bill.url;
  return `https://www.congress.gov/bill/${bill.congress}th-congress/${slug(bill.bill_type)}/${bill.number}`;
}

/** The committee system codes, as chamber and name.
 *
 *  A lookup of published codes, not a guess: a code that is not in this
 *  table comes back as itself. "ssju00" on the page is a gap in this list
 *  and reads as one; "Senate — Commerce" under a code that means something
 *  else would not. */
const COMMITTEES: Record<string, string> = {
  hsag: "House — Agriculture",
  hsap: "House — Appropriations",
  hsas: "House — Armed Services",
  hsba: "House — Financial Services",
  hsbu: "House — Budget",
  hsed: "House — Education and the Workforce",
  hsfa: "House — Foreign Affairs",
  hsgo: "House — Oversight and Government Reform",
  hsha: "House — House Administration",
  hshm: "House — Homeland Security",
  hsif: "House — Energy and Commerce",
  hsii: "House — Natural Resources",
  hsju: "House — Judiciary",
  hspw: "House — Transportation and Infrastructure",
  hsru: "House — Rules",
  hssm: "House — Small Business",
  hsso: "House — Ethics",
  hssy: "House — Science, Space, and Technology",
  hsvr: "House — Veterans' Affairs",
  hswm: "House — Ways and Means",
  hlig: "House — Intelligence",
  ssaf: "Senate — Agriculture, Nutrition, and Forestry",
  ssap: "Senate — Appropriations",
  ssas: "Senate — Armed Services",
  ssbk: "Senate — Banking, Housing, and Urban Affairs",
  ssbu: "Senate — Budget",
  sscm: "Senate — Commerce, Science, and Transportation",
  sseg: "Senate — Energy and Natural Resources",
  ssev: "Senate — Environment and Public Works",
  ssfi: "Senate — Finance",
  ssfr: "Senate — Foreign Relations",
  ssga: "Senate — Homeland Security and Governmental Affairs",
  sshr: "Senate — Health, Education, Labor, and Pensions",
  ssju: "Senate — Judiciary",
  ssra: "Senate — Rules and Administration",
  sssb: "Senate — Small Business and Entrepreneurship",
  ssva: "Senate — Veterans' Affairs",
  scia: "Senate — Indian Affairs",
  slin: "Senate — Intelligence",
  slet: "Senate — Ethics",
  spag: "Senate — Aging",
  jsec: "Joint Economic Committee",
  jsta: "Joint Committee on Taxation",
  jslc: "Joint Committee on the Library",
  jspr: "Joint Committee on Printing",
};

/** "ssju00" as "Senate — Judiciary". The last two digits are the
 *  subcommittee, and "00" is the full committee. */
export function committeeName(code: string): string {
  const trimmed = (code ?? "").trim();
  const named = COMMITTEES[trimmed.slice(0, 4).toLowerCase()];
  if (!named) return trimmed;
  const suffix = trimmed.slice(4);
  return suffix && suffix !== "00" ? `${named}, subcommittee` : named;
}

/** congress.gov's own words for how many times somebody asked for a tally.
 *
 *  Written out rather than assembled from a count and an "s", because the
 *  verb moves too: "there has been 1" and "there have been 2". Zero is the
 *  common case and gets a sentence of its own rather than "0 votes". */
export function rollCallSentence(count: number): string {
  const safe = Number.isFinite(count) && count > 0 ? Math.floor(count) : 0;
  if (safe === 0) return "There have been no roll call votes";
  if (safe === 1) return "There has been 1 roll call vote";
  return `There have been ${safe.toLocaleString()} roll call votes`;
}

/** "Passed the House" beats "passed one chamber" when the server has said
 *  which one. The rung's own label is kept everywhere a rate is quoted,
 *  because the rate belongs to the rung and not to the chamber. */
export function reachedLabel(
  stageLabel: string,
  chambers: string[] | null | undefined,
): string {
  const named = chambers ?? [];
  if (named.length !== 1) return stageLabel;
  return `Passed the ${named[0] === "house" ? "House" : "Senate"}`;
}

/** One mark on the rail across the top of a bill's page. */
export type Step = {
  key: string;
  label: string;
  /** What reaching this point means, in a sentence. Null for the two ends,
   *  which need no explaining. */
  blurb: string | null;
  /** How often bills that got this far became law. Null where the server
   *  sent no ladder, and on the two ends, which are not rungs. */
  odds: Odds | null;
  /** `done` is behind it, `here` is where it stands, `ahead` is not reached.
   *  `unknown` is every rung when the server has not said where it stands —
   *  which is not the same as "not reached". */
  state: "done" | "here" | "ahead" | "unknown";
};

/** The four rungs the ladder measures, in order, with the two ends the
 *  ladder does not measure but a reader expects to see. Introduction and
 *  enactment are facts off the record rather than inferences, so they are
 *  drawn from the bill's own fields and carry no rate. */
const RUNGS = ["committee", "reported", "one-chamber", "both-chambers"] as const;

const FALLBACK_LABEL: Record<string, string> = {
  committee: "In committee",
  reported: "Out of committee",
  "one-chamber": "Passed one chamber",
  "both-chambers": "Passed both chambers",
};

/**
 * The rail: six marks from introduction to enactment.
 *
 * More than the ladder's four on purpose. "Introduced" and "Signed into law"
 * are the two points a reader already has in mind, and both are disclosure —
 * an introduction date and a public law number — so putting them at either
 * end costs nothing and makes the four inferred rungs in the middle legible
 * as the part of the journey that is uncertain.
 *
 * `ladder` may be empty and `stage` may be missing. Neither is an error: the
 * rail draws with no rates, or with nowhere marked, and says so.
 *
 * `odds` is the bill's own rate and is a narrower claim than the ladder: it
 * covers the one rung this bill stands on. It is only used where the ladder
 * did not arrive, so the rung never carries two figures for the same thing.
 */
export function journey(
  bill: Pick<BillDetail, "introduced" | "became_law" | "law_number">,
  stage: Stage | null | undefined,
  ladder: Rung[] | null | undefined,
  odds?: Odds | null,
): Step[] {
  const rungs = new Map((ladder ?? []).map((rung) => [rung.key, rung]));
  const enacted = Boolean(bill.became_law || bill.law_number);
  const rank = typeof stage?.rank === "number" ? stage.rank : null;

  /** Where a rung sits relative to the bill. An enacted bill is past all of
   *  them whatever its stage says, because a public law number is a fact and
   *  a stage is a reading of the actions. */
  function state(at: number): Step["state"] {
    if (enacted) return "done";
    if (rank === null) return "unknown";
    if (at < rank) return "done";
    return at === rank ? "here" : "ahead";
  }

  const middle: Step[] = RUNGS.map((key, at) => {
    const rung = rungs.get(key);
    return {
      key,
      label: rung?.label ?? (stage?.key === key ? stage.label : FALLBACK_LABEL[key]),
      blurb: rung?.blurb ?? (stage?.key === key ? (stage.blurb ?? null) : null),
      odds: rung
        ? { became_law: rung.became_law, of: rung.of, share: rung.share }
        : // No ladder, but the server did send this bill's own odds. They
          // belong on the rung it is standing on and on no other.
          (stage?.key === key ? (odds ?? null) : null),
      state: state(at),
    };
  });

  return [
    {
      key: "introduced",
      label: "Introduced",
      blurb: null,
      odds: null,
      state: bill.introduced || rank !== null || enacted ? "done" : "unknown",
    },
    ...middle,
    {
      key: "law",
      label: "Signed into law",
      blurb: null,
      odds: null,
      state: enacted ? "here" : rank === null ? "unknown" : "ahead",
    },
  ];
}

/** The rail for one payload. `ladder` is passed in rather than read off the
 *  bill, because the page can also get it from `/v1/moving-bills`, which has
 *  served the same four rates all along. */
export function rail(bill: BillDetail, ladder?: Rung[] | null): Step[] {
  return journey(bill, bill.stage, bill.ladder ?? ladder, bill.odds);
}

/** What a generated explainer was written from, and how much that is worth.
 *
 *  The distinction the page exists to make. A model handed the full filed
 *  text and a model handed nine words of title produce prose that reads the
 *  same, and only one of them is worth anything. */
export type Grounding = {
  /** Fits after "written from". */
  phrase: string;
  strength: "strong" | "fair" | "weak" | "unknown";
  /** How firmly to hold it, in a sentence. */
  caveat: string;
};

export function grounding(explainer: Explainer | null | undefined): Grounding {
  const said = explainer?.grounding?.trim().toLowerCase() ?? "";

  if (!said) {
    return {
      phrase: "an unrecorded source",
      strength: "unknown",
      caveat:
        "Nothing records what this was written from, so there is no way to weigh it. Read the official summary below instead.",
    };
  }
  if (said.includes("full text") || said.includes("bill text")) {
    return {
      phrase: "the full filed text of the bill",
      strength: "strong",
      caveat: "The strongest grounding there is: the model was given the bill itself.",
    };
  }
  if (said.includes("partial text")) {
    return {
      phrase: "part of the filed text of the bill",
      strength: "strong",
      caveat:
        "The bill was too long to show the model whole, so it saw the complete list of sections and as much of their text as would fit.",
    };
  }
  if (said.includes("summary")) {
    return {
      phrase: "the official summary below",
      strength: "strong",
      caveat:
        "Written from Congress's own summary, which is on this page underneath. You can read the source and check it against this.",
    };
  }
  if (said.includes("heading")) {
    return {
      phrase: "the section headings alone",
      strength: "weak",
      caveat:
        "The model saw what the sections are called and none of what they say. Headings are written to be short, not to be complete.",
    };
  }
  if (said.includes("title")) {
    return {
      phrase: "the bill's title alone",
      strength: "weak",
      // Deliberately says nothing about whether an official summary exists.
      // "Title only" is written when the summariser had none in front of it,
      // which is not quite the same claim, and this box may not make claims
      // about the record.
      caveat:
        "A title is a few words chosen by the sponsor, and a bill regularly does more or less than its title says. An explanation written from one is a guess at what the bill does, and should be read as a guess.",
    };
  }
  return {
    phrase: `the ${said}`,
    strength: "fair",
    caveat: "Weigh it against the official summary and the bill itself before relying on it.",
  };
}

/** What a generated explanation actually has in it.
 *
 *  The server sends `plain_title`, `key_points` and `who_it_affects`, the
 *  same three columns the law summariser writes; a plainer `text` or
 *  `points` is accepted too, so a change of shape on that side leaves a page
 *  with less on it rather than an empty one. */
export type Body = {
  /** A one-line account of what the bill does. Drawn inside the generated
   *  panel and never used as the page's heading. */
  headline: string | null;
  points: string[];
  text: string | null;
  affects: string | null;
};

const words = (value: unknown): string | null => {
  const said = typeof value === "string" ? value.trim() : "";
  return said.length > 0 ? said : null;
};

/** Null when there is nothing to draw. A field that arrived empty is the
 *  same as one that did not arrive at all. */
export function explainerBody(explainer: Explainer | null | undefined): Body | null {
  const listed = [...(explainer?.key_points ?? []), ...(explainer?.points ?? [])]
    .map(words)
    .filter((point): point is string => point !== null);

  const body: Body = {
    headline: words(explainer?.plain_title),
    points: listed,
    text: words(explainer?.text),
    affects: words(explainer?.who_it_affects),
  };

  const empty = !body.headline && body.points.length === 0 && !body.text && !body.affects;
  return empty ? null : body;
}
